// Viewport class for the responsive shell (KSE-0191). One breakpoint, the
// same as Tailwind's `md` (768px): below it the app is a single column with a
// bottom bar and stacked navigation ("compact"); from it up the desktop
// three-pane layout is unchanged. Layout decisions that cannot be expressed
// with `md:` utilities alone (which pane is mounted, history integration, the
// full-screen composer) read this hook so JS and CSS agree on the breakpoint.

import { useSyncExternalStore } from "react";

export const COMPACT_QUERY = "(max-width: 767.98px)";

export interface Viewport {
  compact: boolean;
}

function mediaQuery(query: string): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }
  return window.matchMedia(query);
}

function subscribeCompact(onChange: () => void): () => void {
  const list = mediaQuery(COMPACT_QUERY);
  if (!list) {
    return () => {};
  }
  list.addEventListener("change", onChange);
  return () => list.removeEventListener("change", onChange);
}

function readCompact(): boolean {
  return mediaQuery(COMPACT_QUERY)?.matches === true;
}

// SSR/no-DOM: desktop (the server snapshot never claims a phone).
function readCompactServer(): boolean {
  return false;
}

/** `{ compact }` — true below Tailwind `md`; updates live on resize/rotate. */
export function useViewport(): Viewport {
  const compact = useSyncExternalStore(subscribeCompact, readCompact, readCompactServer);
  return { compact };
}

/**
 * True when the page runs as an installed app (Home Screen / Dock): the
 * `display-mode: standalone` media feature, or Safari's `navigator.standalone`.
 */
export function isStandaloneDisplayMode(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const nav = navigator as Navigator & { standalone?: boolean };
  return mediaQuery("(display-mode: standalone)")?.matches === true || nav.standalone === true;
}
